import React, { useState, useMemo } from 'react';
import { IconWrapper, SvgIcon } from '../../../../Icon';
import * as NoteUI from '../NoteUI';
import styled from 'styled-components';
import NoteActions from '../NoteActions';
import { useNavigate } from 'react-router-dom';
import { getQueryTypePagePath } from '../../../../../utils';
import { queryTypeConfigLookup } from '../../../../../utils/queryTypeLookup';
import { useAppSelector } from '../../../../../redux/hooks';
import { analysisSelector } from '../../../../../redux/selectors/analyses';
import { isAnalysisLoadingSelector } from '../../../../../redux/selectors/loadingAnalyses';
import { ChartLoader, Chart } from '../../../../Chart';
import { ChartMode } from '../../../../Chart/types';
import useScratchpadAnalysis from '../../../../Chart/utils/useScratchpadAnalysis';
import {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from '@dnd-kit/core';
import { IReportNoteFieldsFragment } from '../../../../../utils/apollo/graphqlSchema';

interface IReportNoteProps {
  noteId: string;
  content: IReportNoteFieldsFragment;
  dragHandleProps?: {
    attributes: DraggableAttributes;
    listeners?: DraggableSyntheticListeners;
  };
}

const Container = styled(NoteUI.Container)`
  display: flex;
  flex-direction: column;
  min-height: 300px;
`;

const ChartContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;

  > div {
    flex: 1;
    min-height: 0;
    border: none;
  }
`;

const mode = ChartMode.Compact;

const ReportNote = ({ noteId, content, dragHandleProps }: IReportNoteProps) => {
  const navigate = useNavigate();
  const analysis = content.analysis;
  const { display, id: analysisId, queryType } = analysis;
  const [isHovered, setIsHovered] = useState(false);

  const { scratchpadAnalysisId } = useScratchpadAnalysis({
    analysisId,
    shouldLoad: true,
  });

  const isAnalysisLoadingMemoSelector = useMemo(isAnalysisLoadingSelector, []);
  const isAnalysisLoading = useAppSelector((state) =>
    isAnalysisLoadingMemoSelector(state, scratchpadAnalysisId),
  );
  const currentAnalysisMemoizedSelector = useMemo(analysisSelector, []);
  const currentAnalysis = useAppSelector((state) =>
    currentAnalysisMemoizedSelector(state, scratchpadAnalysisId),
  );
  const queryTypeConfig = queryTypeConfigLookup[queryType];

  const handleClick = () => {
    navigate(getQueryTypePagePath(queryType, analysisId));
  };

  return (
    <Container>
      <NoteUI.Header
        onMouseOver={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <NoteUI.Row>
          <IconWrapper size={16}>
            {isHovered && dragHandleProps && (
              <NoteUI.DragHandle
                name="drag-drop"
                width={12}
                height={12}
                {...dragHandleProps.attributes}
                {...dragHandleProps.listeners}
              />
            )}
            {!isHovered && (
              <SvgIcon name={queryTypeConfig.icon} width={16} height={16} />
            )}
          </IconWrapper>
          <NoteUI.Name onClick={handleClick}>
            {queryTypeConfigLookup[queryType].display}
          </NoteUI.Name>
        </NoteUI.Row>
        <NoteActions noteId={noteId} />
      </NoteUI.Header>
      <ChartContainer>
        {isAnalysisLoading || !currentAnalysis ? (
          <ChartLoader mode={mode} display={display} />
        ) : (
          <Chart analysisId={scratchpadAnalysisId} mode={mode} />
        )}
      </ChartContainer>
    </Container>
  );
};

export default ReportNote;
